/**
 * My Space 简化版 Header
 *
 * 保留：左侧团队选择（GroupInfo）、右侧通知（NotifyButton）、联系客服（ContactUS）、用户菜单（Dropdown）
 * 去除：VIP 区域、下载客户端、上传照片、直播入口等
 *
 * 复制自 src/pages/mainWrapper/Header/index.tsx，独立维护，不影响原 Header。
 */

import React, { useCallback, useMemo, useState } from 'react'
import VipGuard from '../VipGuard'
import { useSelector } from 'react-redux'
// GroupInfo 的样式依赖原 Header 的 SCSS，直接引入复用
import '../../../mainWrapper/Header/index.scss'
import { Layout, Dropdown, Avatar, Tooltip, Button } from 'antd'
import type { MenuProps } from 'antd'
import { DownOutlined } from '@ant-design/icons'
import { useAppDispatch, useAppSelector } from '../../../../app/hooks'
import { logout } from '../../../../actions/main'
import sensors from '../../../../utils/sensors'
import ContactUS from '../../../components/ContactUS'
import NotifyButton from '../../../mainWrapper/Header/NotifyButton'
import GroupInfo from './GroupInfo'
import global_border_help from '../../../../images/header/global_border_help.png'
import icon_verify_auth from '../../../../images/header/icon_verify_auth.png'
import vipBannerImg from '../../../../images/vip-banner-personal.png'
import styles from './index.module.css'

const { Header } = Layout

interface SpaceHeaderProps {
  onTrack?: (clickItem: string) => void
}

const SpaceHeader: React.FC<SpaceHeaderProps> = ({ onTrack }) => {
  const dispatch = useAppDispatch()
  const user = useAppSelector((state: any) => state.main.user)
  const group = useAppSelector((state: any) => state.group)
  const nickname = useAppSelector(
    (state: any) => (state.main.user?.nickname as string) || ''
  )
  const headimgurl = user?.headimgurl as string | undefined
  const hasGroup = !!group.groupId
  const isPersonalVip = useSelector(
    (state: any) => state.mySpace?.personalSpace?.isPersonalVip ?? false
  )
  const vipStatusLoaded = useSelector(
    (state: any) => state.mySpace?.personalSpace?.vipStatusLoaded ?? false
  )
  const isSpaceOpen = useSelector(
    (state: any) => state.mySpace?.personalSpace?.isSpaceOpen ?? false
  )

  const [vipModalVisible, setVipModalVisible] = useState(false)

  const openVipModal = useCallback(() => {
    setVipModalVisible(true)
  }, [])

  const trackVipModal = useCallback((actionType: string) => {
    sensors.track('other_b7765_d8082_completed', {
      actionType,
      fromPlace: 'openVip',
      track_sign: 'aother.b7765.d8082.completed.other_b7765_d8082_completed',
      requirement_id: 608
    })
  }, [])

  const onLogout = useCallback(() => {
    if (onTrack) onTrack('logout')
    sensors.track('web_workgroup_nav_menu_cli', {
      path: 'quit',
      isGroupVIP: group.isVip,
      bool1: isSpaceOpen
    })
    dispatch(logout())
  }, [dispatch, group, onTrack, isSpaceOpen])

  const menuItems: MenuProps['items'] = useMemo(() => {
    const items: MenuProps['items'] = []
    if (hasGroup) {
      items.push({
        key: 'orders',
        label: '我的订单',
        onClick: () => {
          window.location.href = '/orders'
        }
      })
    }
    items.push({ key: 'logout', label: '退出登录', onClick: onLogout })
    return items
  }, [onLogout, hasGroup])

  return (
    <Header className={`main-header ${styles.header}`}>
      {/* 左侧：团队选择 */}
      <div className={`header-left ${styles.left}`}>
        <GroupInfo onTrack={onTrack} />
      </div>

      {/* 中间：个人会员推广横幅（非会员可见） */}
      {vipStatusLoaded && !isPersonalVip && (
        <img
          src={vipBannerImg}
          alt="会员最低6.7元/月起！尽享20+会员权益"
          style={{
            height: 54,
            cursor: 'pointer',
            position: 'absolute',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)'
          }}
          onClick={() => {
            if (onTrack) onTrack('openVip')
            openVipModal()
          }}
        />
      )}
      <VipGuard
        open={vipModalVisible}
        onClose={() => setVipModalVisible(false)}
        onTrack={trackVipModal}
      />

      {/* 右侧：照片验真、通知、客服、用户菜单 */}
      <div className={`header-right ${styles.right}`}>
        {/* 照片验真 — 个人空间特有 */}
        <button
          className={styles.verifyBtn}
          onClick={() => {
            if (onTrack) onTrack('photoVerification')
            sensors.track('web_workgroup_nav_menu_cli', {
              path: 'verifyAuth',
              bool1: isSpaceOpen
            })
            window.location.href = '/verifyAuth/index'
          }}
        >
          <img
            src={icon_verify_auth}
            alt=""
            style={{ width: 20, height: 20 }}
          />
          照片验真
        </button>

        {/* 通知：个人空间无 groupId，直接渲染不做 hasGroup 判断；通知→客服 32px */}
        <div
          className={styles.notifyWrap}
          onClick={() => onTrack?.('notification')}
        >
          <NotifyButton />
        </div>

        <ContactUS fromPage="my_space_header">
          <Tooltip title="联系客服" placement="bottom">
            <Button
              type="link"
              className={styles.serviceBtn}
              onClick={() => {
                if (onTrack) onTrack('contact')
                sensors.track('web_workgroup_nav_menu_cli', {
                  path: 'onlineSupport',
                  isGroupVIP: group.isVip,
                  bool1: isSpaceOpen
                })
              }}
            >
              <img
                src={global_border_help}
                style={{ width: 24, height: 24 }}
                alt="联系客服"
              />
            </Button>
          </Tooltip>
        </ContactUS>

        <Dropdown menu={{ items: menuItems }}>
          <a
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              marginRight: 16
            }}
            onMouseEnter={() => onTrack?.('hoverAvatar')}
          >
            {headimgurl ? (
              <Avatar
                shape="square"
                size={34}
                style={{ borderRadius: 3.4, flexShrink: 0 }}
                src={headimgurl}
              />
            ) : (
              <div
                style={{
                  width: 34,
                  height: 34,
                  borderRadius: 3.4,
                  backgroundColor: '#0070F4',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  fontSize: 12,
                  fontWeight: 600,
                  color: '#fff'
                }}
              >
                {nickname ? nickname.slice(0, 2) : '我'}
              </div>
            )}
            <DownOutlined style={{ fontSize: 16, color: '#8c8c8c' }} />
          </a>
        </Dropdown>
      </div>
    </Header>
  )
}

export default SpaceHeader
